import { useQuery } from '@tanstack/react-query';
import {
  type CostFigures,
  formatMoney,
  formatQuantity,
  type PayrollCostResponse,
  RATE_UNIT_LABELS,
  salaryPeriodLabel,
  toCents,
} from '@k-management/shared';
import { useState } from 'react';
import { Link } from 'react-router';
import { useCan } from '../../auth/AuthProvider.js';
import { type Command, useCommands, usePageTitle } from '../../components/layout/page-chrome.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate, formatTimestamp } from '../../lib/format.js';
import { PayrollLoadDialog } from './PayrollLoadDialog.js';
import { formatHours, PeopleViews, peopleKeys } from './people-ui.js';

/** «+11 %» from last year's €/hour, or nothing. */
function change(current: string | null, previous: string | null): string {
  if (!current || !previous || toCents(previous) === 0) return '—';
  const percent = Math.round(((toCents(current) - toCents(previous)) / toCents(previous)) * 100);
  return `${percent > 0 ? '+' : ''}${percent} %`;
}

const money = (value: string) => formatMoney(value).replace(/,\d\d\s€$/, ' €');

function CostCells({ figures }: { figures: CostFigures }) {
  return (
    <>
      <td className="numeric">{money(figures.gross)}</td>
      <td className="numeric">{money(figures.inpsCompany)}</td>
      <td className="numeric muted">—</td>
      <td className="numeric">{money(figures.tfr)}</td>
      <td className="numeric">{money(figures.other)}</td>
      <td className="numeric muted">—</td>
      <td className="numeric">
        {Number(figures.thirteenthAccrual) ? money(figures.thirteenthAccrual) : '—'}
      </td>
      <td className="numeric">
        <strong>{money(figures.total)}</strong>
      </td>
      <td className="numeric">{formatHours(figures.hours)}</td>
      <td className="numeric">
        <strong>{figures.hourly ? formatMoney(figures.hourly) : '—'}</strong>
      </td>
    </>
  );
}

/**
 * Persone › Costo del personale (24/10/2026, 2.37): the company cost of every
 * employee from the payroll of the consultant, the €/hour on the hours worked,
 * the months loaded with their checks. Only with «persone.costs».
 */
export function PayrollCostPage() {
  usePageTitle('Costo del personale');
  const canLoad = useCan('persone.write');
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [loading, setLoading] = useState(false);
  const cost = useQuery({
    queryKey: peopleKeys.payroll(year),
    queryFn: () => apiRequest<PayrollCostResponse>(`/people/payroll?year=${year}`),
  });
  useCommands(
    canLoad
      ? [
          {
            key: 'load',
            label: 'Carica le paghe del mese…',
            icon: 'plus',
            primary: true,
            onClick: () => setLoading(true),
          } satisfies Command,
        ]
      : [],
  );
  const data = cost.data;
  const years = [now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2];

  return (
    <section className="stack">
      <div className="page-header">
        <div>
          <h1>Persone</h1>
          <p className="muted">
            Costo aziendale di ogni persona dalle paghe del consulente, mese per mese, ed €/ora
            sulle ore lavorate (senza ferie, festività, permessi e malattia).
          </p>
        </div>
        <label>
          Anno{' '}
          <select value={year} onChange={(event) => setYear(Number(event.target.value))}>
            {years.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
        </label>
      </div>
      <PeopleViews current="costo" />
      {cost.isError && <p className="error">{cost.error.message}</p>}
      {data && data.months.length === 0 && (
        <p className="muted">
          Nessun mese caricato per il {year}.
          {canLoad && ' Carica i PDF del consulente con «Carica le paghe del mese…».'}
        </p>
      )}
      {data && data.months.length > 0 && (
        <>
          <div className="kpi-row">
            <div className="card kpi">
              <small className="muted">Costo aziendale {year}</small>
              <strong>{money(data.totals.total)}</strong>
              <small className="muted">
                {data.thirteenthAccrued ? 'con il rateo della tredicesima' : 'tredicesima compresa'}
              </small>
            </div>
            <div className="card kpi">
              <small className="muted">Ore lavorate</small>
              <strong>{formatHours(data.totals.hours)}</strong>
              <small className="muted">ordinarie e straordinari</small>
            </div>
            <div className="card kpi">
              <small className="muted">€/ora medio diretto</small>
              <strong>{data.totals.hourly ? formatMoney(data.totals.hourly) : '—'}</strong>
              <small className="muted">
                {data.previousHourly ? `${year - 1}: ${formatMoney(data.previousHourly)}` : ' '}
              </small>
            </div>
            <div className="card kpi">
              <small className="muted">€/ora pieno</small>
              <strong className="muted">—</strong>
              <small className="muted">costi generali: da definire</small>
            </div>
          </div>
          <div className="card stack">
            <h2>Per persona</h2>
            <div className="table-scroll">
              <table className="data-table cost-table">
                <thead>
                  <tr>
                    <th>Persona</th>
                    <th className="numeric">Mesi</th>
                    <th className="numeric">Lordo</th>
                    <th className="numeric">INPS az.</th>
                    <th className="numeric" title="Dalla 2.39">
                      INAIL
                    </th>
                    <th className="numeric">TFR</th>
                    <th className="numeric" title="Metasalute, buoni pasto, welfare">
                      Altri
                    </th>
                    <th className="numeric" title="Dalla 2.39">
                      Auto
                    </th>
                    <th className="numeric">Rateo 13ª</th>
                    <th className="numeric">Totale</th>
                    <th className="numeric">Ore</th>
                    <th className="numeric">€/ora</th>
                    <th className="numeric">vs {year - 1}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.people.map((person) => (
                    <tr key={person.employeeId}>
                      <td>
                        <Link to={`/organizzazione/persone/${person.employeeId}?sezione=costo`}>
                          {person.name}
                        </Link>
                        {person.partTimePercent !== null && (
                          <span className="pill muted"> PT {person.partTimePercent} %</span>
                        )}
                      </td>
                      <td className="numeric">{person.months}</td>
                      <CostCells figures={person} />
                      <td className="numeric muted">
                        {change(person.hourly, person.previousHourly)}
                      </td>
                    </tr>
                  ))}
                  <tr className="total">
                    <td>Totale</td>
                    <td />
                    <CostCells figures={data.totals} />
                    <td className="numeric muted">
                      {change(data.totals.hourly, data.previousHourly)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="muted small">
              INAIL e auto aziendale arrivano con la 2.39; i rimborsi spese restano fuori dal costo
              orario. «Altri» = Metasalute, buoni pasto e welfare.
            </p>
          </div>
          <div className="card stack">
            <h2>Mesi caricati e quadratura</h2>
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Mese</th>
                    <th className="numeric">Cedolini</th>
                    <th className="numeric">Lordo persone</th>
                    <th className="numeric">Lordo riepilogo</th>
                    <th className="numeric">INPS azienda</th>
                    <th className="numeric">Netti</th>
                    <th>Controlli</th>
                    <th>Caricato</th>
                  </tr>
                </thead>
                <tbody>
                  {data.months.map((month) => (
                    <tr key={month.id}>
                      <td>
                        {month.folderUrl ? (
                          <a href={month.folderUrl} target="_blank" rel="noreferrer">
                            {salaryPeriodLabel(month.year, month.month)}
                          </a>
                        ) : (
                          salaryPeriodLabel(month.year, month.month)
                        )}
                      </td>
                      <td className="numeric">{month.employees}</td>
                      <td className="numeric">{formatMoney(month.gross)}</td>
                      <td className="numeric">{formatMoney(month.grossCost)}</td>
                      <td className="numeric">{formatMoney(month.inpsCompany)}</td>
                      <td className="numeric">{formatMoney(month.net)}</td>
                      <td>
                        {month.warnings === 0 ? (
                          <span className="pill ok">Quadrato</span>
                        ) : (
                          <span
                            className="pill warn"
                            title={month.checks
                              .filter((check) => check.level === 'warn')
                              .map((check) => check.message)
                              .join('\n')}
                          >
                            {month.warnings} da guardare
                          </span>
                        )}
                      </td>
                      <td className="muted small">
                        {formatTimestamp(month.loadedAt)}
                        {month.loadedByName && ` · ${month.loadedByName}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
      {data && data.collaborators.length > 0 && (
        <div className="card stack">
          <h2>Collaboratori esterni</h2>
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Persona</th>
                  <th>Fornitore</th>
                  <th className="numeric">Costo digitato</th>
                  <th className="numeric">Ore al giorno</th>
                  <th className="numeric">€/ora</th>
                  <th>Valido dal</th>
                  <th className="numeric">Ore e costo</th>
                </tr>
              </thead>
              <tbody>
                {data.collaborators.map((collaborator) => (
                  <tr key={collaborator.employeeId}>
                    <td>
                      <Link to={`/organizzazione/persone/${collaborator.employeeId}?sezione=costo`}>
                        {collaborator.name}
                      </Link>
                    </td>
                    <td>{collaborator.subjectName ?? '—'}</td>
                    <td className="numeric">
                      {collaborator.rate
                        ? `${formatMoney(collaborator.rate.amount)} ${RATE_UNIT_LABELS[collaborator.rate.unit]}`
                        : '—'}
                    </td>
                    <td className="numeric">
                      {collaborator.rate?.hoursPerDay
                        ? formatQuantity(collaborator.rate.hoursPerDay)
                        : '—'}
                    </td>
                    <td className="numeric">
                      <strong>
                        {collaborator.rate ? formatMoney(collaborator.rate.hourly) : '—'}
                      </strong>
                    </td>
                    <td>
                      {collaborator.rate ? formatDate(collaborator.rate.validFrom) : 'da digitare'}
                    </td>
                    <td className="numeric muted">con K-Presenze</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="muted small">
            Il costo dei collaboratori con partita IVA si digita nella loro scheda; le ore, e quindi
            il costo del periodo, arriveranno con l’integrazione di K-Presenze.
          </p>
        </div>
      )}
      {loading && (
        <PayrollLoadDialog
          onClose={() => setLoading(false)}
          onSaved={(saved) => setYear(saved.year)}
        />
      )}
    </section>
  );
}
